import {CSSProperties, FC, RefObject, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {FlexBox, useBreakpoint} from "@brg/ui";
import {AudioTime, AudioTitle, AudioTitleText, AudioTitleTrack} from "../styles";
import {ProgressBarLine} from "./ProgressBarLine";
import {useDispatch, useSelector} from "react-redux";
import {
    selectorAudioCurrentTime,
    selectorAudioPostId,
    selectorAudioProjectId,
    selectorAudioTotalTime,
    setAudioCurrentTime,
    setAudioIsPlaying,
    setAudioTotalDuration
} from "../reducers/AudioSlice";
import {LayoutConst} from "../../../../core";
import {useHistory} from "react-router-dom";
import {CreateLinkTo} from "../../../constants/linkTo";

const formatTime = (seconds: number) => {
    return new Date(seconds * 1000).toISOString().substr(14, 5);
};

const measureNaturalWidth = (element: HTMLElement) => {
    const clone = element.cloneNode(true) as HTMLElement;
    clone.style.position = "absolute";
    clone.style.visibility = "hidden";
    clone.style.pointerEvents = "none";
    clone.style.whiteSpace = "nowrap";
    clone.style.maxWidth = "none";
    clone.style.width = "auto";
    clone.style.minWidth = "0";
    clone.style.inset = "0 auto auto 0";
    document.body.appendChild(clone);
    const width = clone.scrollWidth;
    document.body.removeChild(clone);
    return width;
};

export const TimeComponent: FC<{audioRef: RefObject<HTMLAudioElement>, title: string, hidden?: boolean}> = ({audioRef, title, hidden}) => {

    const projectId = useSelector(selectorAudioProjectId)
    const postId = useSelector(selectorAudioPostId)
    const currentTime = useSelector(selectorAudioCurrentTime)
    const totalDuration = useSelector(selectorAudioTotalTime)
    const dispatch = useDispatch()
    const is992 = useBreakpoint(LayoutConst.screen.m);
    const isMobile = useBreakpoint(LayoutConst.screen.s);
    const history = useHistory()
    const titleContainerRef = useRef<HTMLDivElement>(null);
    const titleContentRef = useRef<HTMLSpanElement>(null);
    const [shouldScrollTitle, setShouldScrollTitle] = useState(false);
    const [titleWidth, setTitleWidth] = useState(0);
    const [containerWidth, setContainerWidth] = useState(0);

    useEffect(() => {
        if (!audioRef?.current) return;
        audioRef.current.currentTime = currentTime;
        }, [audioRef?.current])

    const changeTime = (value: number) => {
        if (!audioRef?.current) return;
        audioRef.current.currentTime = value;
    };

    const handleTimeUpdate = () => {
        if (!audioRef?.current) return;
        dispatch(setAudioCurrentTime(audioRef.current.currentTime))
    };
    const handleAudioEnd = () => {
        dispatch(setAudioIsPlaying(false))
    };

    useLayoutEffect(() => {
        if (!audioRef?.current) return;

        const audio = audioRef.current;
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('ended', handleAudioEnd);
        audio.addEventListener('loadedmetadata', () => {
            dispatch(setAudioTotalDuration(audio.duration))
        });

        return () => {
            audio.removeEventListener('timeupdate', handleTimeUpdate);
        };
    }, [audioRef]);

    useLayoutEffect(() => {
        const container = titleContainerRef.current;
        const content = titleContentRef.current;

        if (!container || !content) {
            setShouldScrollTitle(false);
            setTitleWidth(0);
            setContainerWidth(0);
            return;
        }

        const calculateScroll = () => {
            if (!container || !content) return;

            const containerClientWidth = container.clientWidth;
            const naturalContentWidth = measureNaturalWidth(content);
            const distance = Math.max(naturalContentWidth - containerClientWidth, 0);

            setContainerWidth(prev => {
                const next = containerClientWidth;
                return prev === next ? prev : next;
            });
            setTitleWidth(prev => {
                const next = naturalContentWidth;
                return prev === next ? prev : next;
            });
            setShouldScrollTitle(isMobile && distance > 0);
        };

        calculateScroll();

        if (typeof ResizeObserver !== "undefined") {
            const resizeObserver = new ResizeObserver(() => calculateScroll());
            resizeObserver.observe(container);
            resizeObserver.observe(content);

            return () => resizeObserver.disconnect();
        }
    }, [title, isMobile]);

    const marqueeStyle = useMemo(() => {
        if (!shouldScrollTitle || titleWidth <= 0) return undefined;
        const gap = 32;
        const base = Math.max(titleWidth, containerWidth);
        const translate = base + gap;
        const baseSpeed = 40; // pixels per second
        const duration = Math.min(Math.max(translate / baseSpeed, 8), 20);

        return {
            '--marquee-translate': `${translate}px`,
            '--marquee-duration': `${duration}s`
        } as CSSProperties;
    }, [shouldScrollTitle, titleWidth, containerWidth]);

    const paddingRight = useMemo(() => {
        if (!isMobile) return 0;
        return 56;
    }, [isMobile]);

    return <FlexBox
        width={is992 ? 'calc(100% - 32px)' : '100%'}
        justify="space-between"
        direction="column"
        directionMobile="column"
        height={is992 ? 46 : 42}
        style={{
            ...(hidden && {display: "none"}),
            ...(paddingRight ? {paddingRight: `${paddingRight}px`} : {})
        }}
    >
        <AudioTitle
            ref={titleContainerRef}
            isScrolling={shouldScrollTitle}
            onClick={() => history.push(CreateLinkTo.project(projectId) + `#${postId}`)}
        >
            <AudioTitleTrack
                shouldScroll={shouldScrollTitle}
                style={marqueeStyle}
            >
                <AudioTitleText ref={titleContentRef}>{title}</AudioTitleText>
                {shouldScrollTitle && <AudioTitleText aria-hidden="true">{title}</AudioTitleText>}
            </AudioTitleTrack>
        </AudioTitle>
        <FlexBox width="100%" justify="space-between" align="center" offsetBottom={2}>
            <AudioTime>{formatTime(currentTime)}</AudioTime>
            <AudioTime>{formatTime(totalDuration)}</AudioTime>
        </FlexBox>
        <ProgressBarLine onChange={changeTime} value={currentTime} maxValue={totalDuration}/>
    </FlexBox>
}import {CSSProperties, FC, RefObject, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {FlexBox, useBreakpoint} from "@brg/ui";
import {AudioTime, AudioTitle, AudioTitleText, AudioTitleTrack} from "../styles";
import {ProgressBarLine} from "./ProgressBarLine";
import {useDispatch, useSelector} from "react-redux";
import {
    selectorAudioCurrentTime,
    selectorAudioPostId,
    selectorAudioProjectId,
    selectorAudioTotalTime,
    setAudioCurrentTime,
    setAudioIsPlaying,
    setAudioTotalDuration
} from "../reducers/AudioSlice";
import {LayoutConst} from "../../../../core";
import {useHistory} from "react-router-dom";
import {CreateLinkTo} from "../../../constants/linkTo";

const formatTime = (seconds: number) => {
    return new Date(seconds * 1000).toISOString().substr(14, 5);
};

const measureNaturalWidth = (element: HTMLElement) => {
    const clone = element.cloneNode(true) as HTMLElement;
    clone.style.position = "absolute";
    clone.style.visibility = "hidden";
    clone.style.pointerEvents = "none";
    clone.style.whiteSpace = "nowrap";
    clone.style.maxWidth = "none";
    clone.style.width = "auto";
    clone.style.minWidth = "0";
    clone.style.inset = "0 auto auto 0";
    document.body.appendChild(clone);
    const width = clone.scrollWidth;
    document.body.removeChild(clone);
    return width;
};

export const TimeComponent: FC<{audioRef: RefObject<HTMLAudioElement>, title: string, hidden?: boolean}> = ({audioRef, title, hidden}) => {

    const projectId = useSelector(selectorAudioProjectId)
    const postId = useSelector(selectorAudioPostId)
    const currentTime = useSelector(selectorAudioCurrentTime)
    const totalDuration = useSelector(selectorAudioTotalTime)
    const dispatch = useDispatch()
    const is992 = useBreakpoint(LayoutConst.screen.m);
    const isMobile = useBreakpoint(LayoutConst.screen.s);
    const history = useHistory()
    const titleContainerRef = useRef<HTMLDivElement>(null);
    const titleContentRef = useRef<HTMLSpanElement>(null);
    const [shouldScrollTitle, setShouldScrollTitle] = useState(false);
    const [titleWidth, setTitleWidth] = useState(0);
    const [containerWidth, setContainerWidth] = useState(0);

    useEffect(() => {
        if (!audioRef?.current) return;
        audioRef.current.currentTime = currentTime;
        }, [audioRef?.current])

    const changeTime = (value: number) => {
        if (!audioRef?.current) return;
        audioRef.current.currentTime = value;
    };

    const handleTimeUpdate = () => {
        if (!audioRef?.current) return;
        dispatch(setAudioCurrentTime(audioRef.current.currentTime))
    };
    const handleAudioEnd = () => {
        dispatch(setAudioIsPlaying(false))
    };

    useLayoutEffect(() => {
        if (!audioRef?.current) return;

        const audio = audioRef.current;
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('ended', handleAudioEnd);
        audio.addEventListener('loadedmetadata', () => {
            dispatch(setAudioTotalDuration(audio.duration))
        });

        return () => {
            audio.removeEventListener('timeupdate', handleTimeUpdate);
        };
    }, [audioRef]);

    useLayoutEffect(() => {
        const container = titleContainerRef.current;
        const content = titleContentRef.current;

        if (!container || !content) {
            setShouldScrollTitle(false);
            setTitleWidth(0);
            setContainerWidth(0);
            return;
        }

        const calculateScroll = () => {
            if (!container || !content) return;

            const containerClientWidth = container.clientWidth;
            const naturalContentWidth = measureNaturalWidth(content);
            const distance = Math.max(naturalContentWidth - containerClientWidth, 0);

            setContainerWidth(prev => {
                const next = containerClientWidth;
                return prev === next ? prev : next;
            });
            setTitleWidth(prev => {
                const next = naturalContentWidth;
                return prev === next ? prev : next;
            });
            setShouldScrollTitle(isMobile && distance > 0);
        };

        calculateScroll();

        if (typeof ResizeObserver !== "undefined") {
            const resizeObserver = new ResizeObserver(() => calculateScroll());
            resizeObserver.observe(container);
            resizeObserver.observe(content);

            return () => resizeObserver.disconnect();
        }
    }, [title, isMobile]);

    const marqueeStyle = useMemo(() => {
        if (!shouldScrollTitle || titleWidth <= 0) return undefined;
        const gap = 32;
        const base = Math.max(titleWidth, containerWidth);
        const translate = base + gap;
        const baseSpeed = 40; // pixels per second
        const duration = Math.min(Math.max(translate / baseSpeed, 8), 20);

        return {
            '--marquee-translate': `${translate}px`,
            '--marquee-duration': `${duration}s`
        } as CSSProperties;
    }, [shouldScrollTitle, titleWidth, containerWidth]);

    const paddingRight = useMemo(() => {
        if (!isMobile) return 0;
        return 56;
    }, [isMobile]);

    return <FlexBox
        width={is992 ? 'calc(100% - 32px)' : '100%'}
        justify="space-between"
        direction="column"
        directionMobile="column"
        height={is992 ? 46 : 42}
        style={{
            ...(hidden && {display: "none"}),
            ...(paddingRight ? {paddingRight: `${paddingRight}px`} : {})
        }}
    >
        <AudioTitle
            ref={titleContainerRef}
            isScrolling={shouldScrollTitle}
            onClick={() => history.push(CreateLinkTo.project(projectId) + `#${postId}`)}
        >
            <AudioTitleTrack
                shouldScroll={shouldScrollTitle}
                style={marqueeStyle}
            >
                <AudioTitleText ref={titleContentRef}>{title}</AudioTitleText>
                {shouldScrollTitle && <AudioTitleText aria-hidden="true">{title}</AudioTitleText>}
            </AudioTitleTrack>
        </AudioTitle>
        <FlexBox width="100%" justify="space-between" align="center" offsetBottom={2}>
            <AudioTime>{formatTime(currentTime)}</AudioTime>
            <AudioTime>{formatTime(totalDuration)}</AudioTime>
        </FlexBox>
        <ProgressBarLine onChange={changeTime} value={currentTime} maxValue={totalDuration}/>
    </FlexBox>
}